/**
 * ASAP (forward) resolution for a single scheduling node.
 *
 * Pure function. Given a node, its predecessor edges, the prior-iteration
 * end timestamps for those predecessors, and the relevant config, returns
 * a `{ startTs, endTs, reason }` triple.
 *
 * Algorithm per `04-` §16 forward pass:
 *   1. Initial candidate = node.anchorStartTs.
 *      Used when no predecessor and `applyConstraints:false` (preferred-date
 *      mode); when `applyConstraints:true` and no own hard constraint, the
 *      project_start fallback drives downstream constraint resolution.
 *   2. For each predecessor edge: candidate = applyEdgeForward(predEnd, edge).
 *      earliestStart = max(initial, max over all predecessors).
 *   3. Apply hard-constraint lower bound (SNET/MSO/FNET/MFO).
 *   4. earliestEnd = node.calendar.addDuration(earliestStart, duration, durationUnit).
 *
 * `reason` reflects what drove the date:
 *   - some predecessor edge bumped earliestStart    → `{kind:"asap", linkId, sourceId}`
 *   - hard-constraint lower bound bumped it         → `{kind:"constraint"}`
 *   - neither (anchor stays)                        → `{kind:"preferred"}`
 *
 * Preference rule: constraint wins over predecessor wins over preferred —
 * matches v1's `processConstraint` ordering.
 */

import type { ScheduleReason, NormalizedTaskID } from "../types";
import type { SchedNode, SchedEdge } from "../graph/types";
import {
  applyEdgeForwardFromEndpoints,
  applyConstraintLowerBound,
  applyConstraintUpperBound
} from "../analysis/slack";

export interface AsapInputs {
  readonly node: SchedNode;
  readonly predecessorEdges: readonly SchedEdge[];
  /** Map of already-planned predecessor end timestamps (epoch ms). */
  readonly predEnds: ReadonlyMap<NormalizedTaskID, number>;
  /**
   * Map of already-planned predecessor START timestamps (epoch ms). Used to
   * anchor SS/SF edges on the source's real start instead of
   * `end − sourceDuration`, which is wrong for summary sources whose span ≠
   * stored `duration`. Optional for callers (and pure-engine tests) that
   * don't track starts; absence falls back to the end-based reconstruction.
   */
  readonly predStarts?: ReadonlyMap<NormalizedTaskID, number>;
  /** SchedulingConfig.applyConstraints — gates preferred-date semantics. */
  readonly applyConstraints: boolean;
  /**
   * Project start floor (per D-23 / `04-` §17). Used as the implicit
   * earliest-start when `applyConstraints: true` and the node has no
   * predecessor or own hard-constraint lower bound. Without this, an
   * unanchored task can start before `gantt.config.project_start`.
   */
  readonly projectStartTs?: number;
  /**
   * `SchedulingConfig.gapBehavior` — controls how leaves with both an
   * anchor and predecessors resolve. `"preserve"` (default) keeps the
   * leaf at `max(anchor, predEnd)`; the anchor acts as a floor so tasks
   * stay near their user-preferred date. `"compress"` ignores the anchor
   * floor and uses the predecessor-derived value directly, pulling tasks
   * to their earliest feasible start. Mirrors v1's `gap_behavior` flag.
   */
  readonly gapBehavior?: "preserve" | "compress";
}

export interface AsapResult {
  readonly startTs: number;
  readonly endTs: number;
  readonly reason: ScheduleReason;
}

export function resolveAsap(inputs: AsapInputs): AsapResult {
  const { node, predecessorEdges, predEnds, predStarts, applyConstraints, projectStartTs, gapBehavior } = inputs;

  // Anchor-as-floor decision — whether a predecessor may pull the node EARLIER
  // than its anchor (`node.anchorStartTs`; for a summary, the children-derived
  // effective start supplied by `schedule_pass.ts`).
  //
  // The floor is honored only under `gapBehavior: "preserve"` WITH
  // `apply_constraints: false` — pure preserve, where a node stays at its
  // preferred date and a predecessor can only push it LATER, never earlier.
  // It is ignored (predecessor fully positions the node) when:
  //   - `gapBehavior: "compress"` — compress's contract is to move every task
  //     to its earliest feasible start regardless of the preferred date; or
  //   - `apply_constraints: true` — constraint mode lets an explicit link
  //     (negative lag, SS/SF) pull a node before its anchor. v1's
  //     `asap_strategy.resolveRelationDate` only consults the anchor in its
  //     no-predecessor branch — a task with predecessors is positioned purely
  //     by link-driven dates.
  //
  // This is uniform for leaves and summaries. Summaries matter on both sides:
  //   - GS-1689 (`15_...multiple-level`, apply_constraints:true) needs the
  //     ignore-floor path so a summary follows a predecessor backward across
  //     repeated `autoSchedule()` calls (no compounding forward push).
  //   - GS-2330 scenario 2 (`12_...different_calendars`, preserve +
  //     apply_constraints:false) needs the keep-floor path so a summary whose
  //     children already sit past the link target is NOT dragged earlier.
  // A node with no predecessors always uses its anchor.
  const hasPreds = predecessorEdges.length > 0;
  const ignoreAnchorFloor = hasPreds && (gapBehavior === "compress" || applyConstraints);
  let earliestStartTs = ignoreAnchorFloor
    ? Number.NEGATIVE_INFINITY
    : node.anchorStartTs;
  let drivingEdge: SchedEdge | undefined;

  // For a SUMMARY target, FF/SF edges must convert the target END to its START
  // using the real (children-derived) span — `schedule_pass.ts` supplies it as
  // the effective node's anchor span — not the stale stored `duration`.
  const targetSpanMs = node.kind === "summary"
    ? node.anchorEndTs - node.anchorStartTs
    : undefined;

  // A milestone (zero duration) follows the GS-2508 / GS-1340 convention: when
  // its predecessor-driven date lands on a work-interval boundary it stays at
  // that boundary instead of rolling to the next interval. An FF-driven
  // milestone snaps BACKWARD (stays at the predecessor's interval end — e.g.
  // Friday, not Monday); FS/SS/SF snap FORWARD like a normal task start. We
  // snap each edge's candidate by link type, then take the max — reproducing
  // v1's `asap_strategy`, which snaps each link's candidate before maxing.
  const isMilestone = node.kind !== "summary" && node.duration === 0;

  for (const e of predecessorEdges) {
    const predEnd = predEnds.get(e.source);
    if (predEnd === undefined) continue;
    const predStart = predStarts?.get(e.source);
    let candidate = applyEdgeForwardFromEndpoints(predStart, predEnd, e, targetSpanMs);
    if (isMilestone) {
      const dir = e.originalType === "ff" ? "backward" : "forward";
      candidate = node.calendar.snapToWorking(new Date(candidate), dir).getTime();
    }
    if (candidate > earliestStartTs) {
      earliestStartTs = candidate;
      drivingEdge = e;
    }
  }

  // Safety net for the summary-with-predecessors branch when all predecessor
  // ends were missing from `predEnds` (a logically-impossible state for a
  // well-formed toposort, but defensive): fall back to anchor.
  if (!Number.isFinite(earliestStartTs)) {
    earliestStartTs = node.anchorStartTs;
  }

  // Per D-23 / `04-` §17 + v1 `asap_strategy.ts:66-73` parity: project_start
  // shapes the no-predecessor branch.
  //
  // v1's helper (independent of `apply_constraints`):
  //   minStart = project_start  IF  task.start_date <= project_start
  //                              OR  (compress && task.start_date >= project_start)
  // i.e. under preserve, project_start only RAISES tasks anchored before it;
  // under compress, the no-pred task is pulled to project_start regardless of
  // whether the anchor is earlier or later. The compress branch fixes test
  // 051: a no-pred task with anchor past project_start must compress DOWN
  // to project_start, which the earlier `applyConstraints && < projectStart`
  // guard never did.
  //
  // With predecessors, the predecessor candidate already wins over the
  // anchor (see `ignoreAnchorFloor` above), and v1's helper short-circuits
  // on `!relations.length`, so this branch is leaf/anchor-only. We also snap
  // project_start to the node's working time here — v1 does that via
  // `getClosestWorkTime` further down — because the no-pred branch later
  // skips the generic working-time snap (it would otherwise override a
  // user-set anchor). `project_start` is configured, not user-anchored, so
  // snapping it forward is the v1-equivalent behavior.
  let projectStartFloorApplied = false;
  if (!hasPreds && projectStartTs !== undefined) {
    if (gapBehavior === "compress" || earliestStartTs < projectStartTs) {
      earliestStartTs = projectStartTs;
      projectStartFloorApplied = true;
    }
  } else if (applyConstraints && projectStartTs !== undefined && earliestStartTs < projectStartTs) {
    // Preserve the original D-23 floor for the predecessor branch: when
    // applyConstraints is on, project_start is a hard lower bound even if
    // the predecessor-derived candidate would otherwise place the task
    // earlier (unusual — typically only seen with negative-lag SS/SF).
    earliestStartTs = projectStartTs;
  }

  // Snap the resolved start to the node's working time. The predecessor end
  // (and hence the edge-projected candidate) can land on a non-working moment
  // — Friday 17:00 + 0 lag rolls to Saturday 00:00 by midnight rollover, which
  // is non-working in the target's calendar. v1 snaps before storing (e.g.
  // `alap_strategy.ts:57,60,101` uses `getClosestWorkTime`); v2 mirrors that.
  //
  // Skip the snap when there are no predecessors (the anchor is the user's
  // start_date — snapping would override their explicit choice and, after a
  // previous translation that committed a raw cross-calendar timestamp, would
  // conjure a phantom delta on each subsequent `autoSchedule()` call,
  // breaking idempotency — test 34 GS-2485 "no drift on repeat"). Pitfall #9
  // (the SS/FF candidate-equals-anchor non-working case) still snaps because
  // that case has predecessors.
  //
  // Milestones were already snapped per predecessor edge above (the direction
  // depends on link type — FF backward, others forward), so re-snapping
  // forward here would undo the FF "stay at the interval end" behavior.
  const shouldSnap = !isMilestone && (hasPreds || projectStartFloorApplied);
  if (shouldSnap) {
    earliestStartTs = node.calendar
      .snapToWorking(new Date(earliestStartTs), "forward")
      .getTime();
  }

  // Apply hard-constraint lower bound. Note: applyConstraintLowerBound
  // short-circuits when constraintType is ASAP/ALAP or constraintDateTs is
  // undefined — so this is safe to call unconditionally.
  const beforeLowerBound = earliestStartTs;
  earliestStartTs = applyConstraintLowerBound(earliestStartTs, node);
  let constraintDrove = earliestStartTs !== beforeLowerBound;

  // Compute end. For LEAVES: end = start + duration via the node's calendar
  // (no mixed-calendar drift — D-10). For SUMMARIES: the stored `duration`
  // is the user-supplied field on the project task, which is unrelated to
  // the actual span derived from descendants. Use the snapshot's anchor
  // span (`anchorEndTs - anchorStartTs`) instead so the planner's
  // `predEnds[summary]` matches the live `getSubtaskDates(summary).end_date`.
  // Without this, predecessors that link to a summary see a too-small end
  // and successors land too early.
  let endTs;
  if (node.kind === "summary") {
    const span = node.anchorEndTs - node.anchorStartTs;
    endTs = earliestStartTs + span;
  } else {
    endTs = node.duration === 0
      ? earliestStartTs
      : node.calendar
          .addDuration(new Date(earliestStartTs), node.duration, node.durationUnit)
          .getTime();
  }

  // Phase D Day 20+ fix: MFO / FNLT / SNLT / MSO impose UPPER bounds that
  // the forward pass must honor symmetrically to alap.ts's lower-bound
  // re-application (see alap.ts:80-91). Without this, a task whose anchor
  // or predecessor candidate is already past the upper bound is left in
  // violation — the bug behind 11_constraints_with_minute_precision.ts
  // (Group B/C failures from Phase D Day 20 triage).
  //
  // When the upper bound clamps end down, recompute start = end - duration.
  // If the new start falls below the lower bound established above, the
  // schedule is genuinely over-constrained — `plan.ts` (the caller) is
  // responsible for emitting `onAutoScheduleConflict`. Per the design's
  // hard-constraint priority (`04-` §16.4), constraint wins over the
  // predecessor candidate.
  const beforeUpperBound = endTs;
  endTs = applyConstraintUpperBound(endTs, node);
  if (endTs !== beforeUpperBound) {
    earliestStartTs = node.duration === 0
      ? endTs
      : node.calendar
          .addDuration(new Date(endTs), -node.duration, node.durationUnit)
          .getTime();
    constraintDrove = true;
  }

  // Determine reason. Constraint drove takes priority over predecessor drove
  // (the bound was applied last but reflects a higher-priority requirement).
  let reason: ScheduleReason;
  if (constraintDrove) {
    reason = { kind: "constraint" };
  } else if (drivingEdge) {
    reason = { kind: "asap", linkId: drivingEdge.linkId, sourceId: drivingEdge.source };
  } else {
    reason = { kind: "preferred" };
  }

  return { startTs: earliestStartTs, endTs, reason };
}
